import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { get } from "lodash";
import styled from "styled-components/macro";

import client from "../../api/client/SdsWebApiClient";

import { Card, CardTitle, CardDescription, CardButton, SecondaryCardButton } from "./Cards";

import GaReporter, { GaEcommerceAction } from "../../utilities/gaReporter";
import { SET_ERROR } from "../../store/actions/errorActions";
import { getSiteUrl } from "../../utilities/navigationUtils";
import {
    getAppCustomization,
    getCurrentSummaries,
    getFrontendSettings,
    getGuiTranslations,
    getSessionId,
    getShowPrice,
    getTotalPrice,
    getTouchScreen,
} from "../../store/sagas/selectors";
import InputFloatLabel from "../Form/InputFloatLabel";
import { CardData } from "../../models/summary/Config";
import MailIcon from "../../icons/MailIcon.svg";
import { sanitizeHtml } from "../../utilities/htmlSanitizer";
import {
    MailConsentSettings,
    buildConsentOptions,
    collectSelectedConsents,
    isAppendixEnabled,
    unmetRequiredConsents,
} from "./consentLogic";

const mailformat = /^[^@]+@[^@]+\.[^@]+$/;

interface SpanProps {
    required?: boolean;
}

interface CheckboxProps {
    error: boolean;
}

const Container = styled.div<ContainerProps>`
    visibility: ${props => (props.visible ? "visible" : "hidden")};
`;

const ConsentContainer = styled.div`
    display: flex;
    flex-flow: column;
    align-items: flex-start;
    width: 100%;
    color: ${props => props.theme.textColorSecondary};
`;

// Visually hidden but kept in the tab order so keyboard users can toggle these GDPR-gating consents.
// display:none / visibility:hidden would remove it from the tab order; opacity:0 does not.
const Checkbox = styled.input`
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    margin: 0;
`;

const CheckBoxStyled = styled.span<CheckboxProps>`
    width: 20px;
    height: 20px;
    background: #eee;
    border-radius: 4px;
    ${props => (props.error ? "border: solid thin red;" : "")}
    :after {
        content: "";
        position: absolute;
        display: none;
        left: 6px;
        top: 2px;
        width: 5px;
        height: 10px;
        border: solid white;
        border-width: 0 3px 3px 0;
        transform: rotate(45deg);
    }
`;

const CheckboxLabel = styled.label`
    font-family: ${props => props.theme.fontFamily};
    font-size: 12px;
    align-self: flex-start;
    color: ${props => props.theme.textColorSecondary};
    margin: 2px;
    margin-top: 8px;
    text-align: left;
    display: flex;
    position: relative;
    cursor: pointer;

    &:hover input ~ ${CheckBoxStyled} {
        background: ${({ theme }) => get(theme, "summary.card.button.hoverColor", "#223861")};
    }

    input:checked + ${CheckBoxStyled} {
        background: ${({ theme }) => get(theme, "summary.card.button.backgroundColor", "#223861")};
        :after {
            display: block;
        }
    }

    input:focus-visible + ${CheckBoxStyled} {
        outline: 2px solid ${({ theme }) => get(theme, "summary.card.button.backgroundColor", "#223861")};
        outline-offset: 2px;
    }
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}
`;

const CheckSpan = styled.span<SpanProps>`
    position: relative;
    margin-left: 5px;
    max-width: calc(100% - 35px);

    ${props =>
        props.required
            ? `
        margin-left: 12px;
        ::before {
            position: absolute;
            left: -8px;
            content: '*';
        }`
            : ``}
`;

const ConsentSpan = styled.span`
    font-family: Roboto;
    font-size: 12px;
    align-self: flex-start;
    color: rgba(0, 0, 0, 0.6);
    margin: 2px;
    margin-top: 8px;
    text-align: left;
    display: flex;
    position: relative;
`;

const EmailInputContainer = styled.div`
    display: flex;
    flex-flow: column;
    align-items: center;
    width: 100%;
    color: ${props => props.theme.textColorSecondary};
`;

const InputError = styled.span`
    font-family: ${props => props.theme.fontFamily};
    font-size: 12px;
    align-self: flex-start;
    color: red;
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}
`;

const FeedbackContainer = styled.div<ContainerProps>`
    visibility: ${props => (props.visible ? "visible" : "hidden")};
    ${({ theme }) => theme.bp.xs`
        width: calc(100% - 44px);
        padding: 20px;
        height: calc(100% - 40px);
    `};
    ${({ theme }) => theme.bp.sm`
        width: calc(100% - 24px);
        padding: 10px;
        height: calc(100% - 20px);
    `};
    ${({ theme }) => theme.bp.xxl`
        width: calc(100% - 64px);
    `};
    position: absolute;
    bottom: 0;
    padding: 10px;
    height: calc(100% - 20px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
`;

const FeedbackContent = styled.div``;

const SentIcon = styled.div`
    margin-top: 15px;
    svg {
        fill: currentColor;
        color: var(--primary-color);
`;

interface ContainerProps {
    visible: boolean;
}

const SendSummaryCard = ({ config }: { config: CardData }) => {
    const dispatch = useDispatch();
    const sessionId = useSelector(getSessionId);
    const frontendSettings = useSelector(getFrontendSettings);
    const showPrice = useSelector(getShowPrice);
    const guiTranslations = useSelector(getGuiTranslations);
    const summaryGroups = useSelector(getCurrentSummaries);
    const isTouchScreen = useSelector(getTouchScreen);
    const totalPrice = useSelector(getTotalPrice);
    const appCustomization = useSelector(getAppCustomization);

    // appCustomization is null for sessions without an account (no customization.json) — guard so the card degrades to no consent boxes instead of crashing.
    const consentSettings = (appCustomization?.FrontendSettings || {}) as MailConsentSettings;
    const consentOptions = buildConsentOptions(consentSettings);
    const appendixEnabled = isAppendixEnabled(consentSettings);

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [isDisabled, setIsDisabled] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [checkedConsents, setCheckedConsents] = useState<Record<string, boolean>>({});
    // Keys of required consents left unticked on the last send attempt; drives the red error box.
    const [consentErrors, setConsentErrors] = useState<string[]>([]);

    const toggleConsent = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        setCheckedConsents(prev => ({ ...prev, [key]: checked }));
        // Clear the required-consent error as soon as the box is ticked, rather than waiting for the next Send.
        if (checked) {
            setConsentErrors(prev => prev.filter(k => k !== key));
        }
    };

    const sendCustomerEmail = async () => {
        try {
            if (!email || !email.match(mailformat)) {
                setEmailError(guiTranslations.get("PDF_TOOLTIP_INVALIDEMAIL"));
                return;
            }

            setEmailError("");

            const unmetConsents = unmetRequiredConsents(consentOptions, checkedConsents);
            setConsentErrors(unmetConsents);
            if (unmetConsents.length > 0) return;

            if (!sessionId || !frontendSettings) return;

            setIsDisabled(true);

            const consents = collectSelectedConsents(consentOptions, checkedConsents, appendixEnabled);

            const items = summaryGroups.flatMap(g => g.items);
            const siteUrl = getSiteUrl();
            await client.sendCustomerMailPost(sessionId, email, undefined, !showPrice, siteUrl, consents);

            setEmailSent(true);

            GaReporter.event("cta_summary_email", "CTA");
            GaReporter.trackProductCheckout(
                GaEcommerceAction.Purchase,
                "SUMMARY_EMAIL",
                items,
                showPrice ? totalPrice : null
            );
        } catch (e) {
            dispatch({
                type: SET_ERROR,
                error: {
                    message: guiTranslations.get("PDF_TOOLTIP_EMAILFAILURE"),
                },
            });
        }
        setTimeout(() => {
            setIsDisabled(false);
        }, 2000);
    };

    return (
        <Card>
            <Container visible={!emailSent}>
                <CardTitle>{config.title}</CardTitle>
                <CardDescription dangerouslySetInnerHTML={{ __html: sanitizeHtml(config.description) }} />
                <EmailInputContainer>
                    <InputFloatLabel
                        name={guiTranslations.get("PDF_TEXT_EMAIL")}
                        error={emailError !== ""}
                        setEvent={setEmail}
                        autoComplete={isTouchScreen ? "new-password" : "email"}
                    />
                    {emailError !== "" ? <InputError>{emailError}</InputError> : null}
                </EmailInputContainer>

                <ConsentContainer>
                    {consentOptions.map(option => (
                        <CheckboxLabel key={option.key}>
                            <Checkbox
                                type="checkbox"
                                checked={!!checkedConsents[option.key]}
                                onChange={toggleConsent(option.key)}
                                aria-invalid={consentErrors.includes(option.key)}
                            />
                            <CheckBoxStyled error={consentErrors.includes(option.key)} />
                            <CheckSpan required={option.required}>{guiTranslations.get(option.key)}</CheckSpan>
                        </CheckboxLabel>
                    ))}

                    {/* MAIL_CONSENT_APPENDIX is informational text (no checkbox); recorded whenever enabled. */}
                    {appendixEnabled ? <ConsentSpan>{guiTranslations.get("MAIL_CONSENT_APPENDIX")}</ConsentSpan> : null}
                </ConsentContainer>
                <CardButton
                    isDisabled={isDisabled}
                    onClick={() => {
                        sendCustomerEmail();
                    }}
                >
                    <p>{config.buttonText}</p>
                </CardButton>
            </Container>

            <FeedbackContainer visible={emailSent}>
                <CardTitle>{config.title}</CardTitle>
                <FeedbackContent>
                    <SentIcon>
                        <MailIcon width={40} />
                    </SentIcon>
                </FeedbackContent>
                <CardDescription>
                    {config.emailSentDescription} {email}
                </CardDescription>
                <SecondaryCardButton
                    onClick={() => {
                        setEmailSent(false);
                        setIsDisabled(false);
                    }}
                >
                    <p>{config.feedbackButtonText}</p>
                </SecondaryCardButton>
            </FeedbackContainer>
        </Card>
    );
};

export default SendSummaryCard;
